import React, { useState, Fragment, useRef, useEffect, useCallback } from 'react';
import { Listbox, Transition, Switch } from '@headlessui/react';
import { ChevronDownIcon, ArrowPathIcon, XMarkIcon, PencilSquareIcon, DocumentDuplicateIcon, CheckIcon, LanguageIcon, ArrowDownTrayIcon } from '@heroicons/react/20/solid';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { useSubRoles } from '../hooks/useSubRoles';
import { useRoleTranslation } from '../hooks/useRoleTranslation';
import { useNavigate, useLocation } from 'react-router-dom';
import ErrorNotification from '../components/common/ErrorNotification';
import AudioRecorder from '../components/common/AudioRecorder';
import HelpPopover from '../components/common/HelpPopover';
import Tooltip from '../components/common/Tooltip';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import ChatGPTService from '../services/ChatGPTService';
import LoadingOverlay from '../components/common/LoadingOverlay';
import { apiClient } from '../services/apiClient';
import InsuranceForm from '../components/insurance/InsuranceForm';
import MedidentNeForm from '../components/insurance/MedidentNeForm';
import MedidentBeForm from '../components/insurance/MedidentBeForm';
import MedidentGeForm from '../components/insurance/MedidentGeForm';
import MedidentVsCcForm from '../components/insurance/MedidentVsCcForm';
import MedidentVsSsForm from '../components/insurance/MedidentVsSsForm';
import { hasInsuranceFormRole, hasMedidentNeFormRole, hasMedidentBeFormRole, hasMedidentGeFormRole, hasMedidentVsCcFormRole, hasMedidentVsSsFormRole } from '../constants/insuranceRoles';
import { normalizeTeethListFields } from '../utils/teethList';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const Consultation = () => {
  const { t } = useTranslation('common');
  const { getRoleLabel } = useRoleTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [currentHistoryId, setCurrentHistoryId] = useState(null);
  const [sessionAudioIds, setSessionAudioIds] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [transcriptSegments, setTranscriptSegments] = useState([]);
  //let transcription  = '';// = transcriptSegments.join(' ');
  const [error, setError] = useState('');
  const [shouldResetRecorder, setShouldResetRecorder] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [patientReport, setPatientReport] = useState('');
  const [fileReport, setFileReport] = useState('');
  const [lastTranscript, setLastTranscript] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedReports, setGeneratedReports] = useState({
    dictaphone: false,
    report: false,
    mail: false,
    quote: false,
    insurance: false,
    medidentNe: false,
    medidentBe: false,
    medidentGe: false,
    medidentVsCc: false,
    medidentVsSs: false,
  });
  // eslint-disable-next-line no-unused-vars
  const [newTranscript, setNewTranscript] = useState('');
  const [showNewTranscript, setShowNewTranscript] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isProcessingTranscript, setIsProcessingTranscript] = useState(false);
  const [dictaphoneText, setDictaphoneText] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [isFormattingTranscript, setIsFormattingTranscript] = useState(false);
  const transcriptionRef = useRef(null);
  const [lastCursorPosition, setLastCursorPosition] = useState(null);
  const [isDirectEditing, setIsDirectEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isProfessionalReport, setIsProfessionalReport] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(user?.preferences?.language || 'fr');
  const [translatedText, setTranslatedText] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [accumulatedTranscript, setAccumulatedTranscript] = useState('');
  const accumulatedTranscriptRef = useRef('');
  const [isProcessingFinalTranscript, setIsProcessingFinalTranscript] = useState(false);
  const audioRecorderRef = useRef(null);
  const audioRecorderMainRef = useRef(null);
  const insuranceFormRef = useRef(null);
  const medidentGeFormRef = useRef(null);
  const dictaphoneQuillRef = useRef(null);
  const reportQuillRef = useRef(null);
  const mailQuillRef = useRef(null);
  const [insuranceData, setInsuranceData] = useState(null);
  const [medidentNeData, setMedidentNeData] = useState(null);
  const [medidentBeData, setMedidentBeData] = useState(null);
  const [medidentGeData, setMedidentGeData] = useState(null);
  const [medidentVsCcData, setMedidentVsCcData] = useState(null);
  const [medidentVsSsData, setMedidentVsSsData] = useState(null);
  const [dictaphoneVisited, setDictaphoneVisited] = useState(false);
  const [lastDictaphoneSource, setLastDictaphoneSource] = useState('');
  const [lastReportSource, setLastReportSource] = useState('');
  const [lastMailSource, setLastMailSource] = useState('');
  const [lastInsuranceSource, setLastInsuranceSource] = useState('');
  const [lastMedidentNeSource, setLastMedidentNeSource] = useState('');
  const [lastMedidentBeSource, setLastMedidentBeSource] = useState('');
  const [lastMedidentGeSource, setLastMedidentGeSource] = useState('');
  const [lastMedidentVsCcSource, setLastMedidentVsCcSource] = useState('');
  const [lastMedidentVsSsSource, setLastMedidentVsSsSource] = useState('');
  const [baseTranscriptText, setBaseTranscriptText] = useState('');
  const [usageLimitReached, setUsageLimitReached] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [quoteText, setQuoteText] = useState('');
  const [lastQuoteSource, setLastQuoteSource] = useState('');
  const quoteQuillRef = useRef(null);

  // Helper: add line breaks at end of sentences for display in Dictaphone
  const addLineBreaksAtSentenceEnd = useCallback((text) => {
    if (!text || typeof text !== 'string') return text;
    // Insert a double <br/> after sentence-ending punctuation if not already followed by a <br>
    return text.replace(/([.!?])([\s\u00A0]+)(?!(<br\s*\/?>))/g, '$1<br/>' );
  }, []);

  // Check usage limit on component mount
  useEffect(() => {
    const checkUsageLimit = async () => {
      try {
        const limitCheck = await apiClient.get('/api/consultation-history/check-limit');
        setUsageData(limitCheck);
        if (!limitCheck.allowed) {
          setUsageLimitReached(true);
        }
      } catch (error) {
        console.error('Error checking usage limit:', error);
        // Don't block user if check fails
      }
    };

    checkUsageLimit();
  }, []);

  // Function to save consultation history
  const saveConsultationHistory = useCallback(async () => {
    // Only save if we have some content and a role selected
    if (!selectedRole || (!baseTranscriptText && !dictaphoneText && !fileReport && !patientReport && !insuranceData && !medidentNeData && !medidentBeData && !medidentGeData && !medidentVsCcData && !medidentVsSsData && !quoteText)) {
      return;
    }

    try {
      // Check usage limit before creating new consultation
      if (!currentHistoryId) {
        try {
          const limitCheck = await apiClient.get('/api/consultation-history/check-limit');
          if (!limitCheck.allowed) {
            setError(t('consultation.limitReached', {
              current: limitCheck.current,
              limit: limitCheck.limit,
              remaining: limitCheck.remaining
            }, `Limite mensuelle atteinte. Vous avez utilisé ${limitCheck.current}/${limitCheck.limit} consultations. ${limitCheck.remaining} consultations restantes.`));
            return;
          }
        } catch (limitError) {
          console.error('Error checking usage limit:', limitError);
          // Continue with save if limit check fails (don't block user)
        }
      }

      const historyData = {
        selectedRole,
        baseTranscript: baseTranscriptText,
        dictaphoneText,
        fileReport,
        patientReport,
        insuranceData,
        medidentNeData,
        medidentBeData,
        medidentGeData,
        medidentVsCcData,
        medidentVsSsData,
        quoteText
      };

      if (currentHistoryId) {
        // Update existing history entry
        await apiClient.put(`/api/consultation-history/${currentHistoryId}`, historyData);
        if (sessionAudioIds.length) {
          try {
            await apiClient.post('/api/transcription/audio/attach', {
              consultationHistoryId: currentHistoryId,
              audioIds: sessionAudioIds,
            });
            setSessionAudioIds([]);
          } catch (attachErr) {
            console.error('Error attaching audio files:', attachErr);
          }
        }
      } else {
        // Create new history entry
        const response = await apiClient.post('/api/consultation-history', historyData);
        setCurrentHistoryId(response.id);
        if (sessionAudioIds.length && response.id) {
          try {
            await apiClient.post('/api/transcription/audio/attach', {
              consultationHistoryId: response.id,
              audioIds: sessionAudioIds,
            });
            setSessionAudioIds([]);
          } catch (attachErr) {
            console.error('Error attaching audio files:', attachErr);
          }
        }
      }
    } catch (err) {
      console.error('Error saving consultation history:', err);
      // Don't show error to user, just log it
    }
  }, [selectedRole, baseTranscriptText, dictaphoneText, fileReport, patientReport, insuranceData, medidentNeData, medidentBeData, medidentGeData, medidentVsCcData, medidentVsSsData, quoteText, currentHistoryId, sessionAudioIds, t]);

  // Restore from history data on mount if available
  useEffect(() => {
    const historyData = location.state?.historyData;
    if (historyData) {
      const roleFromHistory = historyData.selected_role;
      setSelectedRole(roleFromHistory);
      
      // Save role from history to localStorage for future use
      if (user?.email && roleFromHistory) {
        const storageKey = `consultation_selected_role_${user.email}`;
        localStorage.setItem(storageKey, roleFromHistory);
      }
      
      setBaseTranscriptText(historyData.base_transcript || '');
      setTranscriptSegments([(historyData.base_transcript || ''), '']);
      setDictaphoneText(historyData.dictaphone_text || '');
      setFileReport(historyData.file_report || '');
      setPatientReport(historyData.patient_report || '');
      setInsuranceData(historyData.insurance_data || null);
      setMedidentNeData(historyData.medident_ne_data || null);
      setMedidentBeData(historyData.medident_be_data || null);
      setMedidentGeData(historyData.medident_ge_data || null);
      setMedidentVsCcData(historyData.medident_vs_cc_data || null);
      setMedidentVsSsData(historyData.medident_vs_ss_data || null);
      setQuoteText(historyData.quote_text || '');
      setCurrentHistoryId(historyData.id);
      setDictaphoneVisited(!!historyData.dictaphone_text);
      
      // Set appropriate generated reports flags
      setGeneratedReports({
        dictaphone: !!historyData.dictaphone_text,
        report: !!historyData.file_report,
        mail: !!historyData.patient_report,
        quote: !!historyData.quote_text,
        insurance: !!historyData.insurance_data,
        medidentNe: !!historyData.medident_ne_data,
        medidentBe: !!historyData.medident_be_data,
        medidentGe: !!historyData.medident_ge_data,
        medidentVsCc: !!historyData.medident_vs_cc_data,
        medidentVsSs: !!historyData.medident_vs_ss_data,
      });

      // Initialize source tracking to prevent regeneration
      if (historyData.base_transcript) {
        setLastDictaphoneSource(historyData.base_transcript);
        setLastQuoteSource(historyData.base_transcript);
      }
      if (historyData.dictaphone_text) {
        setLastReportSource(historyData.dictaphone_text);
        setLastMailSource(historyData.dictaphone_text);
        setLastInsuranceSource(historyData.dictaphone_text);
        setLastMedidentNeSource(historyData.dictaphone_text);
        setLastMedidentBeSource(historyData.dictaphone_text);
        setLastMedidentGeSource(historyData.dictaphone_text);
        setLastMedidentVsCcSource(historyData.dictaphone_text);
        setLastMedidentVsSsSource(historyData.dictaphone_text);
      }

      // Clear the location state to prevent re-loading on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location, user?.email]);

  // Auto-save on significant changes (debounced)
  useEffect(() => {
    if (!selectedRole) return;
    
    const timer = setTimeout(() => {
      saveConsultationHistory();
    }, 2000); // Save after 2 seconds of inactivity

    return () => clearTimeout(timer);
  }, [selectedRole, baseTranscriptText, dictaphoneText, fileReport, patientReport, insuranceData, medidentNeData, medidentBeData, medidentGeData, medidentVsCcData, medidentVsSsData, quoteText, saveConsultationHistory]);


  // Add keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Ctrl+E (or Cmd+E on Mac)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'e') {
        e.preventDefault();
        setIsDirectEditing(true);
        setTranscriptSegments([]);
        
        // Use setTimeout to ensure the div exists after state updates
        setTimeout(() => {
          if (transcriptionRef.current) {
            transcriptionRef.current.focus();
            
            // Set cursor at the end of the content
            const selection = window.getSelection();
            const range = document.createRange();
            const content = transcriptionRef.current;
            
            if (content.childNodes.length > 0) {
              const lastNode = content.childNodes[content.childNodes.length - 1];
              range.setStart(lastNode, lastNode.length);
              range.setEnd(lastNode, lastNode.length);
            } else {
              range.setStart(content, 0);
              range.setEnd(content, 0);
            }
            
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }, 0);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Configuration de l'éditeur Quill
  const modules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, false] }],
        ['bold', 'italic', 'underline'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link'],
        ['table'],
        ['clean']
      ]
    },
    clipboard: {
      matchVisual: false
    },
    table: true
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline',
    'color', 'background',
    'list',
    'link',
    'table',
    'table-row',
    'table-cell'
  ];

  const { subRoles, loading: loadingRoles, error: rolesError } = useSubRoles(user?.group);

  // Load selected role from localStorage on mount
  useEffect(() => {
    // Only load from localStorage if:
    // 1. User is available
    // 2. SubRoles are loaded
    // 3. No role is currently selected
    // 4. No history data was loaded (history data takes precedence)
    if (user?.email && subRoles.length > 0 && !selectedRole && !location.state?.historyData) {
      const storageKey = `consultation_selected_role_${user.email}`;
      const savedRole = localStorage.getItem(storageKey);
      
      // Validate that saved role is still in available subRoles
      if (savedRole && subRoles.includes(savedRole)) {
        setSelectedRole(savedRole);
      } else if (subRoles.length > 0) {
        // Fallback to first available role if saved role is invalid
        setSelectedRole(subRoles[0]);
      }
    } else if (subRoles.length > 0 && !selectedRole) {
      // Fallback: set first role if no localStorage data
      setSelectedRole(subRoles[0]);
    }
  }, [user?.email, subRoles, selectedRole, location.state?.historyData]);

  // Save selected role to localStorage whenever it changes
  useEffect(() => {
    if (user?.email && selectedRole) {
      const storageKey = `consultation_selected_role_${user.email}`;
      localStorage.setItem(storageKey, selectedRole);
    }
  }, [user?.email, selectedRole]);

  const fetchKeywords = useCallback(async (role) => {
    try {
      setLoading(true);
      const data = await apiClient.get(`/api/keywords/${role}`);
      setKeywords(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching keywords:', err);
      setError(t('settings.keywords.error.fetch'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (selectedRole) {
      fetchKeywords(selectedRole);
    }
      }, [selectedRole, fetchKeywords]);

  const steps = [
    { key: 'recording', name: t('consultation.steps.recording') },
    { key: 'dictaphone', name: t('consultation.steps.dictaphone') },
    { key: 'report', name: (selectedRole === 'osteo' || selectedRole === 'physio') ? t('consultation.steps.reportPhysio') : t('consultation.steps.report') },
    { key: 'mail', name: (selectedRole === 'osteo' || selectedRole === 'physio') ? t('consultation.steps.feedbackPhysio') : t('consultation.steps.feedback') },
    { key: 'quote', name: (selectedRole === 'osteo' || selectedRole === 'physio') ? t('consultation.steps.quotePhysio') : t('consultation.steps.quote') },
    ...(hasInsuranceFormRole(selectedRole) ? [{ key: 'insurance', name: t('consultation.steps.insurance') }] : []),
    ...(hasMedidentNeFormRole(selectedRole) ? [{ key: 'medident-ne', name: t('consultation.steps.medidentNe') }] : []),
    ...(hasMedidentBeFormRole(selectedRole) ? [{ key: 'medident-be', name: t('consultation.steps.medidentBe') }] : []),
    ...(hasMedidentGeFormRole(selectedRole) ? [{ key: 'medident-ge', name: t('consultation.steps.medidentGe') }] : []),
    ...(hasMedidentVsCcFormRole(selectedRole) ? [{ key: 'medident-vs-cc', name: t('consultation.steps.medidentVsCc') }] : []),
    ...(hasMedidentVsSsFormRole(selectedRole) ? [{ key: 'medident-vs-ss', name: t('consultation.steps.medidentVsSs') }] : []),
  ].map((step, index) => ({ ...step, id: index + 1 }));

  const getStepKey = (stepId) => steps.find((s) => s.id === stepId)?.key;
  const currentStepKey = getStepKey(currentStep);

  const handleReset = async () => {
    // Save current consultation to history before resetting
    await saveConsultationHistory();
    
    // Reset all state
    setTranscriptSegments([]);
    setCurrentStep(1);
    setError('');
    setShouldResetRecorder(true);
    setGeneratedReports({
      dictaphone: false,
      report: false,
      mail: false,
      quote: false,
      insurance: false,
      medidentNe: false,
      medidentBe: false,
      medidentGe: false,
      medidentVsCc: false,
      medidentVsSs: false,
    });
    setDictaphoneText('');
    setPatientReport('');
    setFileReport('');
    setQuoteText('');
    // Clear translation-related state to avoid stale copies after reset
    setTranslatedText('');
    setShowTranslation(false);
    setShowCopySuccess(false);
    setIsTranslating(false);
    setSelectedLanguage(user?.preferences?.language || 'fr');
    setTimeout(() => setShouldResetRecorder(false), 100);
    setDictaphoneVisited(false);
    setBaseTranscriptText('');
    setLastInsuranceSource('');
    setLastMedidentNeSource('');
    setLastMedidentBeSource('');
    setLastMedidentGeSource('');
    setLastMedidentVsCcSource('');
    setLastMedidentVsSsSource('');
    setLastReportSource('');
    setLastMailSource('');
    setLastDictaphoneSource('');
    setLastQuoteSource('');
    setInsuranceData(null);
    setMedidentNeData(null);
    setMedidentBeData(null);
    setMedidentGeData(null);
    setMedidentVsCcData(null);
    setMedidentVsSsData(null);
    // Reset history ID to create a new entry on next save
    setCurrentHistoryId(null);
    setSessionAudioIds([]);
  };

  // Restore the original handleTranscriptionUpdate for AudioRecorder
  const handleTranscriptionUpdate = (textArray) => {
    // If textArray is shorter than transcriptSegments, it means a new session started (browser reset), so append
    if (textArray.length < transcriptSegments.length) {
      setTranscriptSegments(prev => [...prev, ...textArray.slice(prev.length)]);
    } else {
      setTranscriptSegments(textArray);
    }
    // Update the base transcript live only on step 1
    if (currentStep === 1) {
      setBaseTranscriptText(textArray.join(' '));
    }
  };

  // Keep the new handleTranscriptionEdit for manual editing
  const handleTranscriptionEdit = (e) => {
    const content = e.currentTarget.textContent;
    setTranscriptSegments([content]);
    setBaseTranscriptText(content);
    
    // Save current cursor position
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      setLastCursorPosition({
        start: range.startOffset,
        end: range.endOffset
      });
    }
  };

  useEffect(() => {
    if (lastCursorPosition && transcriptionRef.current) {
      const selection = window.getSelection();
      const range = document.createRange();
      const textNode = transcriptionRef.current.firstChild || transcriptionRef.current;
      
      try {
        range.setStart(textNode, lastCursorPosition.start);
        range.setEnd(textNode, lastCursorPosition.end);
        selection.removeAllRanges();
        selection.addRange(range);
      } catch (e) {
        console.warn('Could not restore cursor position:', e);
      }
    }
  }, [transcriptSegments, lastCursorPosition, transcriptionRef]);

  const handleRecordingError = (errorMessage) => {
    const text =
      typeof errorMessage === 'string'
        ? errorMessage
        : errorMessage?.message || errorMessage?.toString?.() || t('consultation.errors.generationFailed');
    setError(text);
  };

  const handleStepClick = async (step) => {
    const transcription = baseTranscriptText;
    const stepKey = getStepKey(step);

    if (step > 1 && (!transcription || transcription.trim() === '')) {
      setError(t('consultation.errors.transcriptionRequired'));
      return;
    }

    // Enforce dictaphone step before generating other outputs
    if (stepKey && !['recording', 'dictaphone'].includes(stepKey) && !dictaphoneVisited) {
      setError(t('consultation.errors.dictaphoneRequired'));
      return;
    }

    handleCloseTranscriptPanel();
    handleCloseAudioRecorder();

    // Reset or preload transient transcript buffers based on destination step
    if (stepKey === 'recording') {
      setNewTranscript(baseTranscriptText);
      setAccumulatedTranscript(baseTranscriptText);
      setTranscriptSegments([baseTranscriptText, '']);
    } else {
      setNewTranscript('');
      setAccumulatedTranscript('');
      setTranscriptSegments(['']);
    }

    const shouldGenDictaphone = stepKey === 'dictaphone' && (!generatedReports.dictaphone || transcription !== lastDictaphoneSource);
    const shouldGenReport = stepKey === 'report' && (!generatedReports.report || dictaphoneText !== lastReportSource);
    const shouldGenMail = stepKey === 'mail' && (!generatedReports.mail || dictaphoneText !== lastMailSource);
    const shouldGenQuote = stepKey === 'quote' && (!generatedReports.quote || transcription !== lastQuoteSource);
    const shouldGenInsurance = stepKey === 'insurance' && (!generatedReports.insurance || dictaphoneText !== lastInsuranceSource);
    const shouldGenMedidentNe = stepKey === 'medident-ne' && (!generatedReports.medidentNe || dictaphoneText !== lastMedidentNeSource);
    const shouldGenMedidentBe = stepKey === 'medident-be' && (!generatedReports.medidentBe || dictaphoneText !== lastMedidentBeSource);
    const shouldGenMedidentGe = stepKey === 'medident-ge' && (!generatedReports.medidentGe || dictaphoneText !== lastMedidentGeSource);
    const shouldGenMedidentVsCc = stepKey === 'medident-vs-cc' && (!generatedReports.medidentVsCc || dictaphoneText !== lastMedidentVsCcSource);
    const shouldGenMedidentVsSs = stepKey === 'medident-vs-ss' && (!generatedReports.medidentVsSs || dictaphoneText !== lastMedidentVsSsSource);

    if (shouldGenDictaphone || shouldGenReport || shouldGenMail || shouldGenQuote || shouldGenInsurance || shouldGenMedidentNe || shouldGenMedidentBe || shouldGenMedidentGe || shouldGenMedidentVsCc || shouldGenMedidentVsSs) {
      try {
        setIsGenerating(true);
        setError(null);

        if (stepKey === 'dictaphone') {
          const dictaphoneStructured = await ChatGPTService.generateStructuredTranscript(
            selectedRole,
            'dictaphone',
            user.preferences?.language || 'fr',
            transcription
          );
          const formattedTranscript = addLineBreaksAtSentenceEnd(dictaphoneStructured);
          if (!formattedTranscript && formattedTranscript !== '') {
            throw new Error('Invalid response from dictaphone generation service');
          }
          setDictaphoneText(formattedTranscript);
          setGeneratedReports(prev => ({ ...prev, dictaphone: true }));
          setDictaphoneVisited(true);
          setLastDictaphoneSource(transcription);
        } else if (stepKey === 'report') {
          const fileStructuredTranscript = await ChatGPTService.generateStructuredTranscript(
            selectedRole,
            'report',
            user.preferences?.language || 'fr',
            dictaphoneText
          );
          if (!fileStructuredTranscript) {
            throw new Error('Invalid response from report generation service');
          }
          setFileReport(fileStructuredTranscript);
          setGeneratedReports(prev => ({ ...prev, report: true }));
          setLastReportSource(dictaphoneText);
        } else if (stepKey === 'mail') {
          const mailStructuredTranscript = await ChatGPTService.generateStructuredTranscript(
            selectedRole,
            'mail',
            user.preferences?.language || 'fr',
            dictaphoneText
          );
          if (!mailStructuredTranscript) {
            throw new Error('Invalid response from mail generation service');
          }
          setPatientReport(mailStructuredTranscript);
          setTranslatedText('');
          setShowTranslation(false);
          setGeneratedReports(prev => ({ ...prev, mail: true }));
          setLastMailSource(dictaphoneText);
        } else if (stepKey === 'quote') {
          const quoteStructured = await ChatGPTService.generateStructuredTranscript(
            selectedRole,
            'quote',
            user.preferences?.language || 'fr',
            transcription
          );
          if (!quoteStructured) {
            throw new Error('Invalid response from quote generation service');
          }
          setQuoteText(quoteStructured);
          setGeneratedReports(prev => ({ ...prev, quote: true }));
          setLastQuoteSource(transcription);
        } else if (stepKey === 'insurance') {
          const parsed = await generateAndCompleteLamalTranscript(
            dictaphoneText,
            user.preferences?.language || 'fr'
          );
          setInsuranceData(parsed);
          setGeneratedReports(prev => ({ ...prev, insurance: true }));
          setLastInsuranceSource(dictaphoneText);
        } else if (stepKey === 'medident-ne') {
          const parsed = await generateAndCompleteMedidentNeTranscript(
            dictaphoneText,
            user.preferences?.language || 'fr'
          );
          setMedidentNeData(parsed);
          setGeneratedReports(prev => ({ ...prev, medidentNe: true }));
          setLastMedidentNeSource(dictaphoneText);
        } else if (stepKey === 'medident-be') {
          const parsed = await generateAndCompleteMedidentBeTranscript(
            dictaphoneText,
            user.preferences?.language || 'fr'
          );
          setMedidentBeData(parsed);
          setGeneratedReports(prev => ({ ...prev, medidentBe: true }));
          setLastMedidentBeSource(dictaphoneText);
        } else if (stepKey === 'medident-ge') {
          const parsed = await generateAndCompleteMedidentGeTranscript(
            dictaphoneText,
            user.preferences?.language || 'fr'
          );
          setMedidentGeData(parsed);
          setGeneratedReports(prev => ({ ...prev, medidentGe: true }));
          setLastMedidentGeSource(dictaphoneText);
        } else if (stepKey === 'medident-vs-cc') {
          const parsed = await generateAndCompleteMedidentVsCcTranscript(
            dictaphoneText,
            user.preferences?.language || 'fr'
          );
          setMedidentVsCcData(parsed);
          setGeneratedReports(prev => ({ ...prev, medidentVsCc: true }));
          setLastMedidentVsCcSource(dictaphoneText);
        } else if (stepKey === 'medident-vs-ss') {
          const parsed = await generateAndCompleteMedidentVsSsTranscript(
            dictaphoneText,
            user.preferences?.language || 'fr'
          );
          setMedidentVsSsData(parsed);
          setGeneratedReports(prev => ({ ...prev, medidentVsSs: true }));
          setLastMedidentVsSsSource(dictaphoneText);
        }
        setLastTranscript(transcription);
        setCurrentStep(step);
      } catch (error) {
        console.error('Error generating content:', error);
        setError(t('consultation.errors.generationFailed'));
        return;
      } finally {
        setIsGenerating(false);
      }
    } else {
      setCurrentStep(step);
    }
  };

  const handleEditKeywords = () => {
    navigate('/settings');
    setTimeout(() => {
      const keywordsSection = document.querySelector('[data-section="keywords"]');
      if (keywordsSection) {
        keywordsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };


  const handleCopy = async () => {
    try {
      const textToCopy = currentStep === 2 ? dictaphoneText : 
                        currentStep === 3 ? fileReport : 
                        currentStep === 4 ? patientReport :
                        currentStep === 5 ? quoteText : '';
      
      // Create a temporary div and add it to the document (needed for rich text copy)
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.innerHTML = textToCopy;
      document.body.appendChild(tempDiv);

      // Select the content
      const range = document.createRange();
      range.selectNode(tempDiv);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);

      // Execute copy command
      document.execCommand('copy');

      // Cleanup
      selection.removeAllRanges();
      document.body.removeChild(tempDiv);

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
      setError(t('error.copy'));
    }
  };

  const handleFileReportChange = (content) => {
    setFileReport(content);
  };

  const toggleProfessionalFormat = (checked) => {
    const greeting = t('consultation.professionalReport.greeting');
    const farewell = t('consultation.professionalReport.farewell');
    
    // Function to clean the content from any existing greetings/farewells
    const cleanContent = (text) => {
      return text
        .replace(new RegExp(greeting.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), '')
        .replace(new RegExp(farewell.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), '')
        .trim();
    };

    const cleaned = cleanContent(fileReport);
    
    if (checked) {
      setFileReport(greeting + cleaned + farewell);
    } else {
      setFileReport(cleaned);
    }
  };

  const languages = [
    { code: 'en', name: t('consultation.translation.languages.en') },
    { code: 'es', name: t('consultation.translation.languages.es') },
    { code: 'fr', name: t('consultation.translation.languages.fr') },
    { code: 'de', name: t('consultation.translation.languages.de') },
    { code: 'it', name: t('consultation.translation.languages.it') },
    { code: 'pt', name: t('consultation.translation.languages.pt') },
    { code: 'nl', name: t('consultation.translation.languages.nl') },
    { code: 'ru', name: t('consultation.translation.languages.ru') }
  ];

  const handleLanguageChange = async (langCode) => {
    setSelectedLanguage(langCode);
    if (patientReport) {
      try {
        setIsTranslating(true);
        const translated = await ChatGPTService.translateText(patientReport, langCode);
        setTranslatedText(translated);
      } catch (error) {
        console.error('Translation error:', error);
        setError(t('consultation.translation.error'));
      } finally {
        setIsTranslating(false);
      }
    }
  };

  const handleShowTranslation = () => {
    setShowTranslation(true);
  };

  const handleCopyTranslation = async () => {
    try {
      const disclaimer = t('consultation.translation.disclaimer');
      const originalText = currentStep === 2 ? fileReport : patientReport;
      
      // Create a temporary div for formatted content
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      
      // Add content with proper formatting
      tempDiv.innerHTML = `${disclaimer}\n\n${translatedText}\n\n-------\nTexte original:\n\n${originalText}`;
      document.body.appendChild(tempDiv);

      // Select the content
      const range = document.createRange();
      range.selectNode(tempDiv);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);

      // Execute copy command
      document.execCommand('copy');

      // Cleanup
      selection.removeAllRanges();
      document.body.removeChild(tempDiv);
      
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy text:', error);
      setError(t('common.error.copy'));
    }
  };

  const handleNewTranscriptionUpdate = async (textArray) => {
    const fullText = textArray.join(' ');
    setNewTranscript(fullText);
    setShowNewTranscript(true);
    accumulatedTranscriptRef.current = fullText;
    setAccumulatedTranscript(fullText);
    if (currentStep === 1) {
      setDictaphoneVisited(false);
    }
    // Also accumulate in transcriptSegments
    setTranscriptSegments(prev => [...prev, ...textArray.slice(prev.length)]);
  };

  // Helper function to get selection from ReactQuill editor
  const getQuillSelection = (quillRef) => {
    if (!quillRef?.current) return null;
    
    const quill = quillRef.current.getEditor();
    if (!quill) return null;
    
    const selection = quill.getSelection(true);
    if (!selection || selection.length === 0) return null;
    
    // Get plain text content of selection
    const selectedText = quill.getText(selection.index, selection.length);
    if (!selectedText || selectedText.trim().length === 0) return null;
    
    return {
      text: selectedText.trim(),
      index: selection.index,
      length: selection.length
    };
  };

  const resetCorrectionTranscriptBuffers = () => {
    setShowNewTranscript(false);
    setNewTranscript('');
    accumulatedTranscriptRef.current = '';
    setAccumulatedTranscript('');
    setTranscriptSegments(['']);
  };

  const handleRecordingStopped = async () => {
    // Snapshot before any async work: prefer ref (sync) so file-mode Whisper text is not lost
    const correctionText = (accumulatedTranscriptRef.current || accumulatedTranscript || '').trim();
    if (!correctionText) {
      resetCorrectionTranscriptBuffers();
      return;
    }

    try {
      setIsProcessingFinalTranscript(true);

      if (currentStep === 2) {
        // Step 2: improve the base transcript then regenerate dictaphone text
        // Check for selection in dictaphone editor
        const selection = getQuillSelection(dictaphoneQuillRef);
        const originalText = selection ? dictaphoneText : baseTranscriptText;
        
        const improvedTranscript = await ChatGPTService.improveTranscript(
          originalText,
          correctionText,
          selectedRole,
          selectedLanguage,
          'dictaphone',
          selection
        );
        
        if (selection) {
          // If there was a selection, the backend returns the full text with only selected part changed
          // Replace the entire dictaphoneText content
          setDictaphoneText(improvedTranscript);
        } else {
          // No selection: original behavior
          const updatedReport = await ChatGPTService.generateStructuredTranscript(
            selectedRole,
            'dictaphone',
            user.preferences?.language || 'fr',
            improvedTranscript
          );
          setDictaphoneText(addLineBreaksAtSentenceEnd(updatedReport));
          setDictaphoneVisited(true);
          setBaseTranscriptText(improvedTranscript);
        }
      } else if (currentStep === 3) {
        // Step 3: improve ONLY the current report content using the new audio
        const selection = getQuillSelection(reportQuillRef);
        
        const improvedReport = await ChatGPTService.improveTranscript(
          fileReport,
          correctionText,
          selectedRole,
          selectedLanguage,
          'rapport',
          selection
        );
        
        // Backend returns full text with only selected part changed (if selection exists)
        setFileReport(improvedReport);
      } else if (currentStep === 4) {
        // Step 4: improve ONLY the current patient report content
        const selection = getQuillSelection(mailQuillRef);
        
        const improvedMail = await ChatGPTService.improveTranscript(
          patientReport,
          correctionText,
          selectedRole,
          selectedLanguage,
          'retour_patient',
          selection
        );
        
        // Backend returns full text with only selected part changed (if selection exists)
        setPatientReport(improvedMail);
        setTranslatedText('');
        setShowTranslation(false);
      } else if (currentStep === 5) {
        // Step 5: improve ONLY the current quote content
        const selection = getQuillSelection(quoteQuillRef);
        
        const improvedQuote = await ChatGPTService.improveTranscript(
          quoteText,
          correctionText,
          selectedRole,
          selectedLanguage,
          'devis',
          selection
        );
        
        // Backend returns full text with only selected part changed (if selection exists)
        setQuoteText(improvedQuote);
      } else if (currentStepKey === 'insurance') {
        // Prefer live form state (parent insuranceData can lag behind local edits)
        let latestInsuranceData = insuranceData || {};
        const insuranceFormApi = insuranceFormRef.current;
        if (insuranceFormApi) {
          if (typeof insuranceFormApi.flushSave === 'function') {
            latestInsuranceData = insuranceFormApi.flushSave();
          } else if (typeof insuranceFormApi.getFormData === 'function') {
            latestInsuranceData = insuranceFormApi.getFormData();
          }
        }
        const originalJsonText = JSON.stringify(latestInsuranceData);
        const improvedJsonText = await ChatGPTService.improveTranscript(
          originalJsonText,
          correctionText,
          selectedRole,
          selectedLanguage,
          'assurance'
        );
        try {
          const parsed = JSON.parse(improvedJsonText);
          setInsuranceData(normalizeTeethListFields(parsed));
        } catch (e) {
          setError(t('consultation.errors.transcriptProcessingFailed'));
        }
      } else if (currentStepKey === 'medident-ne') {
        const originalJsonText = JSON.stringify(medidentNeData || {});
        const improvedJsonText = await ChatGPTService.improveTranscript(
          originalJsonText,
          correctionText,
          selectedRole,
          selectedLanguage,
          'medident-ne'
        );
        try {
          const parsed = JSON.parse(improvedJsonText);
          setMedidentNeData(normalizeTeethListFields(parsed));
        } catch (e) {
          setError(t('consultation.errors.transcriptProcessingFailed'));
        }
      } else if (currentStepKey === 'medident-be') {
        const originalJsonText = JSON.stringify(medidentBeData || {});
        const improvedJsonText = await ChatGPTService.improveTranscript(
          originalJsonText,
          correctionText,
          selectedRole,
          selectedLanguage,
          'medident-be'
        );
        try {
          const parsed = JSON.parse(improvedJsonText);
          setMedidentBeData(normalizeTeethListFields(parsed));
        } catch (e) {
          setError(t('consultation.errors.transcriptProcessingFailed'));
        }
      } else if (currentStepKey === 'medident-ge') {
        let latestGeData = medidentGeData || { etat_denture: [], traitement_envisage: [] };
        const geFormApi = medidentGeFormRef.current;
        if (geFormApi) {
          if (typeof geFormApi.flushSave === 'function') {
            latestGeData = geFormApi.flushSave();
          } else if (typeof geFormApi.getFormData === 'function') {
            latestGeData = geFormApi.getFormData();
          }
        }
        const originalJsonText = JSON.stringify(latestGeData);
        const improvedJsonText = await ChatGPTService.improveTranscript(
          originalJsonText,
          correctionText,
          selectedRole,
          selectedLanguage,
          'medident-ge'
        );
        try {
          const parsed = JSON.parse(improvedJsonText);
          setMedidentGeData(parsed && typeof parsed === 'object' ? parsed : latestGeData);
        } catch (e) {
          setError(t('consultation.errors.transcriptProcessingFailed'));
        }
      } else if (currentStepKey === 'medident-vs-cc') {
        const originalJsonText = JSON.stringify(medidentVsCcData || {});
        const improvedJsonText = await ChatGPTService.improveTranscript(
          originalJsonText,
          correctionText,
          selectedRole,
          selectedLanguage,
          'medident-vs-cc'
        );
        try {
          const parsed = JSON.parse(improvedJsonText);
          setMedidentVsCcData(normalizeTeethListFields(parsed));
        } catch (e) {
          setError(t('consultation.errors.transcriptProcessingFailed'));
        }
      } else if (currentStepKey === 'medident-vs-ss') {
        const originalJsonText = JSON.stringify(medidentVsSsData || {});
        const improvedJsonText = await ChatGPTService.improveTranscript(
          originalJsonText,
          correctionText,
          selectedRole,
          selectedLanguage,
          'medident-vs-ss'
        );
        try {
          const parsed = JSON.parse(improvedJsonText);
          setMedidentVsSsData(normalizeTeethListFields(parsed));
        } catch (e) {
          setError(t('consultation.errors.transcriptProcessingFailed'));
        }
      }
    } catch (error) {
      console.error('Error processing transcript:', error);
      setError(t('consultation.errors.transcriptProcessingFailed'));
    } finally {
      setIsProcessingFinalTranscript(false);
      // Always clear so the next correction recording does not reuse previous audio text
      resetCorrectionTranscriptBuffers();
    }
  };

  const handleCloseTranscriptPanel = () => {
    if (audioRecorderRef.current) {
      audioRecorderRef.current.stopRecording();
    }
    resetCorrectionTranscriptBuffers();
  };

  const handleCloseAudioRecorder = () => {
    if (audioRecorderMainRef.current) {
      audioRecorderMainRef.current.stopRecording();
    }
  };

  const handleInsuranceFormSave = (data) => {
    setInsuranceData(data);
  };

  const handleMedidentNeFormSave = (data) => {
    setMedidentNeData(data);
  };

  const handleMedidentBeFormSave = (data) => {
    setMedidentBeData(data);
  };

  const handleMedidentGeFormSave = (data) => {
    setMedidentGeData(data);
  };

  const handleMedidentVsCcFormSave = (data) => {
    setMedidentVsCcData(data);
  };

  const handleMedidentVsSsFormSave = (data) => {
    setMedidentVsSsData(data);
  };

  const handleGenerateQuotePDF = async () => {
    if (!quoteText || quoteText.trim() === '') {
      setError(t('consultation.quoteErrors.empty'));
      return;
    }

    try {
      setIsGenerating(true);
      setError(null);

      // Fetch clinic data for header
      let clinicData = null;
      try {
        clinicData = await apiClient.get('/api/clinic');
      } catch (clinicError) {
        console.warn('Could not fetch clinic data:', clinicError);
        // Continue without clinic data
      }

      // Get the Quill editor content as HTML
      const quillEditor = quoteQuillRef.current?.getEditor();
      if (!quillEditor) {
        throw new Error('Quill editor not available');
      }

      // Get HTML content from Quill
      const htmlContent = quillEditor.root.innerHTML;

      // Build header HTML
      let headerHTML = '';
      if (clinicData) {
        const logoHTML = clinicData.logo_image 
          ? `<img src="${clinicData.logo_image}" alt="Logo" class="pdf-header-logo" />`
          : '';
        
        const phoneIcon = `<svg class="pdf-header-phone-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M14.05 6C15.0268 6.19057 15.9244 6.66826 16.6281 7.37194C17.3318 8.07561 17.8095 8.97326 18 9.95M14.05 2C16.0793 2.22544 17.9716 3.13417 19.4163 4.57701C20.8609 6.01984 21.7721 7.91101 22 9.94M18.5 21C9.93959 21 3 14.0604 3 5.5C3 5.11378 3.01413 4.73086 3.04189 4.35173C3.07375 3.91662 3.08968 3.69907 3.2037 3.50103C3.29814 3.33701 3.4655 3.18146 3.63598 3.09925C3.84181 3 4.08188 3 4.56201 3H7.37932C7.78308 3 7.98496 3 8.15802 3.06645C8.31089 3.12515 8.44701 3.22049 8.55442 3.3441C8.67601 3.48403 8.745 3.67376 8.88299 4.05321L10.0491 7.26005C10.2096 7.70153 10.2899 7.92227 10.2763 8.1317C10.2643 8.31637 10.2012 8.49408 10.0942 8.64506C9.97286 8.81628 9.77145 8.93713 9.36863 9.17882L8 10C9.2019 12.6489 11.3501 14.7999 14 16L14.8212 14.6314C15.0629 14.2285 15.1837 14.0271 15.3549 13.9058C15.5059 13.7988 15.6836 13.7357 15.8683 13.7237C16.0777 13.7101 16.2985 13.7904 16.74 13.9509L19.9468 15.117C20.3262 15.255 20.516 15.324 20.6559 15.4456C20.7795 15.553 20.8749 15.6891 20.9335 15.842C21 16.015 21 16.2169 21 16.6207V19.438C21 19.9181 21 20.1582 20.9007 20.364C20.8185 20.5345 20.663 20.7019 20.499 20.7963C20.3009 20.9103 20.0834 20.9262 19.6483 20.9581C19.2691 20.9859 18.8862 21 18.5 21Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;
        
        headerHTML = `
          <div class="pdf-header">
            <div class="pdf-header-left">
              <div class="pdf-header-cabinet-name">${clinicData.cabinet_name || ''}</div>
              <div class="pdf-header-place">${clinicData.place || ''}</div>
              <div class="pdf-header-phone">${phoneIcon}${clinicData.phone || ''}</div>
            </div>
            <div class="pdf-header-right">
              ${logoHTML}
            </div>
          </div>
        `;
      }

      // Create a temporary div to render the Quill content with professional styling
      const tempDiv = document.createElement('div');
      // Convert A4 dimensions to pixels (A4: 210mm x 297mm, at 96 DPI: ~794px x 1123px)
      const a4WidthPx = 794; // 210mm at 96 DPI
      const paddingPx = 40; // Reduced padding for more content space
      
      // Base container styles
      tempDiv.style.position = 'absolute';
      tempDiv.style.top = '0';
      tempDiv.style.left = '0';
      tempDiv.style.width = `${a4WidthPx}px`;
      tempDiv.style.padding = `${paddingPx}px`;
      tempDiv.style.fontFamily = 'Arial, Helvetica, sans-serif';
      tempDiv.style.fontSize = '11pt';
      tempDiv.style.lineHeight = '1.6';
      tempDiv.style.color = '#000000';
      tempDiv.style.backgroundColor = '#FFFFFF';
      tempDiv.style.zIndex = '9999';
      tempDiv.style.opacity = '1';
      tempDiv.style.pointerEvents = 'none';
      tempDiv.style.overflow = 'visible';
      tempDiv.style.transform = 'translateX(-10000px)';
      
      // Inject comprehensive CSS styles for professional PDF rendering
      const style = document.createElement('style');
      style.textContent = `
        /* PDF Header */
        .pdf-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 40px;
          padding-bottom: 20px;
        }
        .pdf-header-left {
          flex: 1;
        }
        .pdf-header-cabinet-name {
          font-size: 16pt;
          font-weight: bold;
          margin-bottom: 8px;
          color: #1a1a1a;
        }
        .pdf-header-place {
          font-size: 11pt;
          margin-bottom: 5px;
          color: #333;
        }
        .pdf-header-phone {
          font-size: 11pt;
          color: #333;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .pdf-header-phone-icon {
          flex-shrink: 0;
          width: 14px;
          height: 14px;
          color: #333;
          display: inline-block;
          align-self: flex-end;
          margin-bottom: 0px;
        }
        .pdf-header-right {
          flex-shrink: 0;
          margin-left: 20px;
        }
        .pdf-header-logo {
          max-width: 150px;
          max-height: 80px;
          object-fit: contain;
        }
        
        /* Typography */
        h1 {
          font-size: 24pt;
          font-weight: bold;
          margin: 0 0 20px 0;
          padding-bottom: 10px;
          border-bottom: 2px solid #333;
          color: #1a1a1a;
        }
        h2 {
          font-size: 18pt;
          font-weight: bold;
          margin: 25px 0 15px 0;
          color: #2a2a2a;
        }
        h3 {
          font-size: 14pt;
          font-weight: bold;
          margin: 20px 0 10px 0;
          color: #3a3a3a;
        }
        p {
          margin: 10px 0;
          text-align: justify;
        }
        
        /* Tables - Professional styling */
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
          font-size: 10pt;
          border: 1px solid #333;
        }
        table thead {
          background-color: #f5f5f5;
        }
        table th {
          padding: 12px 8px;
          text-align: left;
          font-weight: bold;
          border: 1px solid #333;
          background-color: #e8e8e8;
          color: #000;
        }
        table td {
          padding: 10px 8px;
          border: 1px solid #666;
          vertical-align: top;
        }
        table tbody tr:nth-child(even) {
          background-color: #fafafa;
        }
        table tbody tr:hover {
          background-color: #f0f0f0;
        }
        
        /* Lists */
        ul, ol {
          margin: 10px 0;
          padding-left: 30px;
        }
        li {
          margin: 5px 0;
        }
        
        /* Blockquotes */
        blockquote {
          margin: 15px 0;
          padding: 10px 20px;
          border-left: 4px solid #333;
          background-color: #f9f9f9;
          font-style: italic;
        }
        
        /* Code blocks */
        pre {
          margin: 15px 0;
          padding: 15px;
          background-color: #f5f5f5;
          border: 1px solid #ddd;
          border-radius: 4px;
          overflow-x: auto;
          font-family: 'Courier New', monospace;
          font-size: 9pt;
        }
        code {
          font-family: 'Courier New', monospace;
          font-size: 9pt;
          background-color: #f5f5f5;
          padding: 2px 4px;
          border-radius: 3px;
        }
        
        /* Links */
        a {
          color: #0066cc;
          text-decoration: underline;
        }
        
        /* Images */
        img {
          max-width: 100%;
          height: auto;
          margin: 15px 0;
        }
        
        /* Horizontal rules */
        hr {
          border: none;
          border-top: 1px solid #ccc;
          margin: 20px 0;
        }
        
        /* Strong and emphasis */
        strong {
          font-weight: bold;
        }
        em {
          font-style: italic;
        }
        
        /* Spacing utilities */
        .ql-align-center {
          text-align: center;
        }
        .ql-align-right {
          text-align: right;
        }
        .ql-align-justify {
          text-align: justify;
        }
      `;
      document.head.appendChild(style);
      
      // Combine header and content
      tempDiv.innerHTML = headerHTML + htmlContent;
      document.body.appendChild(tempDiv);

      // Wait for content to render and images to load
      await new Promise(resolve => setTimeout(resolve, 500));

      // Convert HTML to canvas using html2canvas
      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#FFFFFF'
      });

      // Calculate PDF dimensions
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Create PDF
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Convert canvas to image data
      const imgData = canvas.toDataURL('image/png');

      // Add image to PDF with pagination if needed
      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add new pages if content is taller than one page
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        doc.addPage();
        doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0];
      const filename = `devis-${date}.pdf`;

      // Clean up temporary div and style before saving
      if (document.body.contains(tempDiv)) {
        document.body.removeChild(tempDiv);
      }
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }

      // Save PDF
      doc.save(filename);
      setIsGenerating(false);
    } catch (error) {
      console.error('Error generating quote PDF:', error);
      setError(t('consultation.quoteErrors.pdfGenerationFailed'));
      setIsGenerating(false);
    }
  };

  // Helper to generate and complete lamal transcript with admin info
  const generateAndCompleteLamalTranscript = async (transcript, language = 'fr') => {
    const lamalTranscript = await ChatGPTService.generateStructuredTranscript(
      selectedRole,
      'lamal',
      language,
      transcript
    );
    let parsed;
    try {
      parsed = typeof lamalTranscript === 'string' ? JSON.parse(lamalTranscript) : lamalTranscript;
    } catch (e) {
      parsed = {};
    }
    console.log('[LAMAL] JSON déterminé par le LLM (avant enrichissement):', parsed);
    // Fetch clinic info
    let clinicInfo = {};
    try {
      clinicInfo = await apiClient.get('/api/clinic');
    } catch (e) {
      clinicInfo = {};
    }
    // Fetch employee info
    let employeeInfo = {};
    try {
      employeeInfo = await apiClient.get('/api/profile');
    } catch (e) {
      employeeInfo = {};
    }
    // Fill administrative fields if empty
    parsed['dentist-office'] = parsed['dentist-office'] || clinicInfo.cabinet_name || '';
    parsed['gln-nr'] = parsed['gln-nr'] || clinicInfo.num_gkn || '';
    parsed['rcc-nr'] = parsed['rcc-nr'] || clinicInfo.num_rcc || '';
    parsed['phone-nr'] = parsed['phone-nr'] || clinicInfo.phone || '';
    parsed['employee-name'] = parsed['employee-name'] || employeeInfo.employee_name || '';
    parsed['dentist'] = parsed['dentist'] || employeeInfo.medecin || '';
    const normalized = normalizeTeethListFields(parsed);
    console.log('[LAMAL] JSON passé au formulaire:', normalized);
    return normalized;
  };

  const generateAndCompleteMedidentNeTranscript = async (transcript, language = 'fr') => {
    const medidentTranscript = await ChatGPTService.generateStructuredTranscript(
      selectedRole,
      'medident-ne',
      language,
      transcript
    );
    let parsed;
    try {
      parsed = typeof medidentTranscript === 'string' ? JSON.parse(medidentTranscript) : medidentTranscript;
    } catch (e) {
      parsed = {};
    }

    let clinicInfo = {};
    try {
      clinicInfo = await apiClient.get('/api/clinic');
    } catch (e) {
      clinicInfo = {};
    }
    let employeeInfo = {};
    try {
      employeeInfo = await apiClient.get('/api/profile');
    } catch (e) {
      employeeInfo = {};
    }

    parsed['dentist-office'] = parsed['dentist-office'] || clinicInfo.cabinet_name || '';
    parsed['rcc-nr'] = parsed['rcc-nr'] || clinicInfo.num_rcc || '';
    parsed['phone-nr'] = parsed['phone-nr'] || clinicInfo.phone || '';
    parsed['employee-name'] = parsed['employee-name'] || employeeInfo.employee_name || '';
    parsed['dentist'] = parsed['dentist'] || employeeInfo.medecin || '';
    if (!parsed['place-date']) {
      const today = new Date().toLocaleDateString('fr-CH');
      parsed['place-date'] = `Neuchâtel, le ${today}`;
    }
    parsed['is-lamal'] = parsed['is-lamal'] || 'non';
    parsed['is-laa'] = parsed['is-laa'] || 'non';
    parsed['is-pain-only'] = parsed['is-pain-only'] || 'non';

    return normalizeTeethListFields(parsed);
  };

  const generateAndCompleteMedidentBeTranscript = async (transcript, language = 'fr') => {
    const medidentTranscript = await ChatGPTService.generateStructuredTranscript(
      selectedRole,
      'medident-be',
      language,
      transcript
    );
    let parsed;
    try {
      parsed = typeof medidentTranscript === 'string' ? JSON.parse(medidentTranscript) : medidentTranscript;
    } catch (e) {
      parsed = {};
    }

    let clinicInfo = {};
    try {
      clinicInfo = await apiClient.get('/api/clinic');
    } catch (e) {
      clinicInfo = {};
    }
    let employeeInfo = {};
    try {
      employeeInfo = await apiClient.get('/api/profile');
    } catch (e) {
      employeeInfo = {};
    }

    parsed['clinic-name'] = parsed['clinic-name'] || clinicInfo.cabinet_name || '';
    parsed['clinic-zip-city'] = parsed['clinic-zip-city'] || clinicInfo.place || '';
    parsed['treating-dentist'] = parsed['treating-dentist'] || employeeInfo.medecin || employeeInfo.employee_name || '';
    if (!parsed['place-date']) {
      const today = new Date().toLocaleDateString('fr-CH');
      const place = clinicInfo.place || 'Berne';
      parsed['place-date'] = `${place}, le ${today}`;
    }

    return normalizeTeethListFields(parsed);
  };

  const generateAndCompleteMedidentGeTranscript = async (transcript, language = 'fr') => {
    const medidentTranscript = await ChatGPTService.generateStructuredTranscript(
      selectedRole,
      'medident-ge',
      language,
      transcript
    );
    let parsed;
    try {
      parsed = typeof medidentTranscript === 'string' ? JSON.parse(medidentTranscript) : medidentTranscript;
    } catch (e) {
      parsed = {};
    }
    return {
      etat_denture: Array.isArray(parsed?.etat_denture) ? parsed.etat_denture : [],
      traitement_envisage: Array.isArray(parsed?.traitement_envisage) ? parsed.traitement_envisage : [],
    };
  };

  const generateAndCompleteMedidentVsCcTranscript = async (transcript, language = 'fr') => {
    const medidentTranscript = await ChatGPTService.generateStructuredTranscript(
      selectedRole,
      'medident-vs-cc',
      language,
      transcript
    );
    let parsed;
    try {
      parsed = typeof medidentTranscript === 'string' ? JSON.parse(medidentTranscript) : medidentTranscript;
    } catch (e) {
      parsed = {};
    }

    let clinicInfo = {};
    try {
      clinicInfo = await apiClient.get('/api/clinic');
    } catch (e) {
      clinicInfo = {};
    }
    let employeeInfo = {};
    try {
      employeeInfo = await apiClient.get('/api/profile');
    } catch (e) {
      employeeInfo = {};
    }

    const dentistLine = [employeeInfo.medecin || employeeInfo.employee_name, clinicInfo.cabinet_name, clinicInfo.place]
      .filter(Boolean)
      .join('\n');
    parsed['medecin-dentiste'] = parsed['medecin-dentiste'] || dentistLine;
    parsed.telephone = parsed.telephone || clinicInfo.phone || '';
    parsed['annexe-devis-dentiste'] = parsed['annexe-devis-dentiste'] !== false;
    ['fumeur', 'cas-lamal-opas', 'cas-accident', 'prestations-complementaires'].forEach((k) => {
      parsed[k] = parsed[k] || 'non';
    });
    if (!parsed['lieu-date']) {
      const today = new Date().toLocaleDateString('fr-CH');
      const place = clinicInfo.place || 'Sion';
      parsed['lieu-date'] = `${place}, le ${today}`;
    }
    return normalizeTeethListFields(parsed);
  };

  const generateAndCompleteMedidentVsSsTranscript = async (transcript, language = 'fr') => {
    const medidentTranscript = await ChatGPTService.generateStructuredTranscript(
      selectedRole,
      'medident-vs-ss',
      language,
      transcript
    );
    let parsed;
    try {
      parsed = typeof medidentTranscript === 'string' ? JSON.parse(medidentTranscript) : medidentTranscript;
    } catch (e) {
      parsed = {};
    }

    let clinicInfo = {};
    try {
      clinicInfo = await apiClient.get('/api/clinic');
    } catch (e) {
      clinicInfo = {};
    }
    let employeeInfo = {};
    try {
      employeeInfo = await apiClient.get('/api/profile');
    } catch (e) {
      employeeInfo = {};
    }

    parsed['centre-medico-social'] = parsed['centre-medico-social'] || clinicInfo.cabinet_name || '';
    parsed['medecin-dentiste'] = parsed['medecin-dentiste'] || employeeInfo.medecin || employeeInfo.employee_name || '';
    ['cas-lamal-opas', 'cas-accident', 'prestations-complementaires'].forEach((k) => {
      parsed[k] = parsed[k] || 'non';
    });
    if (!parsed['lieu-date']) {
      const today = new Date().toLocaleDateString('fr-CH');
      const place = clinicInfo.place || 'Sion';
      parsed['lieu-date'] = `${place}, le ${today}`;
    }
    return normalizeTeethListFields(parsed);
  };

  const renderAudioRecorder = () => (
    <div className="relative">
      {/* New Transcript Panel */}
      {showNewTranscript && accumulatedTranscript && (
        <div className="absolute bottom-full w-full mb-4">
          <div className="bg-white rounded-lg shadow-xl p-4 border border-gray-200">
            <div className="mb-2 flex justify-between items-center">
              <h3 className="text-sm font-medium text-gray-900">
                {t('consultation.transcription.newTranscript')}
              </h3>
              {isProcessingTranscript ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                  <span className="text-sm text-gray-600">{t('consultation.transcription.processing')}</span>
                </div>
              ) : (
                <button
                  onClick={handleCloseTranscriptPanel}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
            <p className="text-sm text-gray-600">
              {accumulatedTranscript}
            </p>
          </div>
        </div>
      )}
      
      {/* Audio Recorder */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <AudioRecorder
            ref={audioRecorderRef}
            onTranscriptionUpdate={handleNewTranscriptionUpdate}
            onError={handleRecordingError}
            onReset={shouldResetRecorder}
            onStop={handleRecordingStopped}
            onAudioSaved={(audioId) => {
              setSessionAudioIds((prev) => (prev.includes(audioId) ? prev : [...prev, audioId]));
            }}
            consultationHistoryId={currentHistoryId}
            recordingContext="correction"
            helperText={currentStep > 1 ? t('consultation.transcription.completeOrCorrect') : undefined}
            additionalHelperText={
              currentStepKey === 'dictaphone' ? t('consultation.transcription.helperTextDictaphone') :
              currentStepKey === 'report' ? t('consultation.transcription.helperTextReport') :
              currentStepKey === 'mail' ? t('consultation.transcription.helperTextFeedback') :
              currentStepKey === 'quote' ? t('consultation.transcription.helperTextQuote') :
              currentStepKey === 'insurance' ? t('consultation.transcription.helperTextInsurance') :
              currentStepKey === 'medident-ne' ? t('consultation.transcription.helperTextMedidentNe') :
              currentStepKey === 'medident-be' ? t('consultation.transcription.helperTextMedidentBe') :
              currentStepKey === 'medident-ge' ? t('consultation.transcription.helperTextMedidentGe') :
              currentStepKey === 'medident-vs-cc' ? t('consultation.transcription.helperTextMedidentVsCc') :
              currentStepKey === 'medident-vs-ss' ? t('consultation.transcription.helperTextMedidentVsSs') :
              undefined
            }
            transcriptSegments={transcriptSegments}
            selectedRole={selectedRole}
          />
        </div>
      </div>
    </div>
  );

  // Render separate editors for each step to maintain their state independently
  const renderEditor = () => {
    if (currentStep === 1) {
      return (
        <>
          {/* Transcription Content */}
          <div className="flex-1 pt-6 pl-6 pr-6 overflow-y-auto">
            <div className="prose prose-gray max-w-none">
              {(transcriptSegments.join(' ') || isDirectEditing) ? (
                <div
                  ref={transcriptionRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={handleTranscriptionEdit}
                  className="focus:outline-none min-h-[150px]"
                  style={{ whiteSpace: 'pre-wrap' }}
                  dangerouslySetInnerHTML={{ __html: baseTranscriptText }}
                />
              ) : (
                <div className="text-center text-gray-500">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                  <p className="text-lg font-medium mb-2">{t('consultation.transcription.noAudioTitle')}</p>
                  <p className="text-sm">{t('consultation.transcription.noAudioDescription')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Audio Recorder */}
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <AudioRecorder
                  ref={audioRecorderMainRef}
                  onTranscriptionUpdate={handleTranscriptionUpdate}
                  onError={handleRecordingError}
                  onReset={shouldResetRecorder}
                  onAudioSaved={(audioId) => {
                    setSessionAudioIds((prev) => (prev.includes(audioId) ? prev : [...prev, audioId]));
                  }}
                  consultationHistoryId={currentHistoryId}
                  recordingContext="initial"
                  transcriptSegments={transcriptSegments}
                  selectedRole={selectedRole}
                />
              </div>
            </div>
          </div>
        </>
      );
    }

    // Render separate editors for steps 2, 3, 4 and 5
    return (
      <div className="flex-1 p-6 overflow-y-auto relative">
        {currentStep === 2 && (
          <div className="bg-gray-100 rounded-3xl shadow-sm h-full flex flex-col">
            <div className="p-6 flex-1 overflow-y-auto">
              <ReactQuill
                ref={dictaphoneQuillRef}
                key="dictaphoneText"
                theme="snow"
                value={dictaphoneText}
                onChange={setDictaphoneText}
                modules={modules}
                formats={formats}
                className="h-[calc(100%-20px)]"
              />
            </div>
            {/* Audio Recorder */}
            {renderAudioRecorder()}
          </div>
        )}
        {currentStep === 3 && (
          <div className="bg-gray-100 rounded-3xl shadow-sm h-full flex flex-col">
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="mb-4 flex items-center space-x-2">
                <Switch
                  checked={isProfessionalReport}
                  onChange={(checked) => {
                    setIsProfessionalReport(checked);
                    toggleProfessionalFormat(checked);
                  }}
                  className={`${
                    isProfessionalReport ? 'bg-[rgb(141,212,209)]' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      isProfessionalReport ? 'translate-x-6' : 'translate-x-1'
                    } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </Switch>
                <span className="text-sm text-gray-700">{t('consultation.professionalReport.switch')}</span>
              </div>
              <ReactQuill
                ref={reportQuillRef}
                key="fileReport"
                theme="snow"
                value={fileReport}
                onChange={handleFileReportChange}
                modules={modules}
                formats={formats}
                className="h-[calc(100%-60px)]"
              />
            </div>
            {/* Audio Recorder */}
            {renderAudioRecorder()}
          </div>
        )}
        {currentStep === 4 && (
          <div className="bg-gray-100 rounded-3xl shadow-sm h-full flex flex-col">
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="mb-4 flex items-center space-x-4">
                <span className="text-sm text-gray-600">{t('consultation.translation.select')}</span>
                <div className="relative w-48">
                  <Listbox value={selectedLanguage} onChange={handleLanguageChange}>
                    {({ open }) => (
                      <>
                        <Listbox.Button className="relative w-full text-left bg-[#E0EAFE] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <span className="block truncate pr-8">
                            {languages.find(lang => lang.code === selectedLanguage)?.name}
                          </span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronDownIcon
                              className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                                open ? 'transform rotate-180' : ''
                              }`}
                              aria-hidden="true"
                            />
                          </span>
                        </Listbox.Button>
                        <Transition
                          show={open}
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {languages.map((language) => (
                              <Listbox.Option
                                key={language.code}
                                className={({ active }) =>
                                  `relative cursor-default select-none py-2 pl-3 pr-9 ${
                                    active ? 'bg-[#E0EAFE] text-gray-900' : 'text-gray-900'
                                  }`
                                }
                                value={language.code}
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                      {language.name}
                                    </span>
                                    {selected && (
                                      <span
                                        className={`absolute inset-y-0 right-0 flex items-center pr-3 ${
                                          active ? 'text-gray-900' : 'text-blue-600'
                                        }`}
                                      >
                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </>
                    )}
                  </Listbox>
                </div>
                {translatedText && (
                  <Tooltip content={t('consultation.translation.tooltip')} position="bottom">
                    <div 
                      className="p-2 rounded-full hover:bg-[#E0EAFE] cursor-pointer transition-colors"
                      onClick={handleShowTranslation}
                    >
                      <LanguageIcon className="h-5 w-5 text-gray-600" />
                    </div>
                  </Tooltip>
                )}
              </div>
              <ReactQuill
                ref={mailQuillRef}
                key="patientReport"
                theme="snow"
                value={patientReport}
                onChange={setPatientReport}
                modules={modules}
                formats={formats}
                className="h-[calc(100%-60px)]"
              />
            </div>
            {/* Audio Recorder */}
            {renderAudioRecorder()}
          </div>
        )}
        {currentStep === 5 && (
          <div className="bg-gray-100 rounded-3xl shadow-sm h-full flex flex-col">
            <div className="p-6 flex-1 overflow-y-auto">
              <ReactQuill
                ref={quoteQuillRef}
                key="quoteText"
                theme="snow"
                value={quoteText}
                onChange={setQuoteText}
                modules={modules}
                formats={formats}
                className="h-[calc(100%-20px)]"
              />
            </div>
            {/* Audio Recorder */}
            {renderAudioRecorder()}
          </div>
        )}
        {currentStepKey === 'insurance' && (
          <div className="bg-gray-100 rounded-3xl shadow-sm h-full flex flex-col">
            <div className="p-6 flex-1 overflow-y-auto">
              <InsuranceForm
                ref={insuranceFormRef}
                initialData={insuranceData}
                onSave={handleInsuranceFormSave}
                selectedRole={selectedRole}
              />
            </div>
            {renderAudioRecorder()}
          </div>
        )}
        {currentStepKey === 'medident-ne' && (
          <div className="bg-gray-100 rounded-3xl shadow-sm h-full flex flex-col">
            <div className="p-6 flex-1 overflow-y-auto">
              <MedidentNeForm
                initialData={medidentNeData}
                onSave={handleMedidentNeFormSave}
              />
            </div>
            {renderAudioRecorder()}
          </div>
        )}
        {currentStepKey === 'medident-be' && (
          <div className="bg-gray-100 rounded-3xl shadow-sm h-full flex flex-col">
            <div className="p-6 flex-1 overflow-y-auto">
              <MedidentBeForm
                initialData={medidentBeData}
                onSave={handleMedidentBeFormSave}
              />
            </div>
            {renderAudioRecorder()}
          </div>
        )}
        {currentStepKey === 'medident-ge' && (
          <div className="bg-gray-100 rounded-3xl shadow-sm h-full flex flex-col">
            <div className="p-6 flex-1 overflow-y-auto">
              <MedidentGeForm
                ref={medidentGeFormRef}
                initialData={medidentGeData}
                onSave={handleMedidentGeFormSave}
              />
            </div>
            {renderAudioRecorder()}
          </div>
        )}
        {currentStepKey === 'medident-vs-cc' && (
          <div className="bg-gray-100 rounded-3xl shadow-sm h-full flex flex-col">
            <div className="p-6 flex-1 overflow-y-auto">
              <MedidentVsCcForm
                initialData={medidentVsCcData}
                onSave={handleMedidentVsCcFormSave}
              />
            </div>
            {renderAudioRecorder()}
          </div>
        )}
        {currentStepKey === 'medident-vs-ss' && (
          <div className="bg-gray-100 rounded-3xl shadow-sm h-full flex flex-col">
            <div className="p-6 flex-1 overflow-y-auto">
              <MedidentVsSsForm
                initialData={medidentVsSsData}
                onSave={handleMedidentVsSsFormSave}
              />
            </div>
            {renderAudioRecorder()}
          </div>
        )}
      </div>
    );
  };

  if (loadingRoles) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (rolesError) {
    return (
      <div className="h-full flex items-center justify-center">
        <ErrorNotification 
          show={true}
          message={t('consultation.errors.rolesFetchFailed')}
          onClose={() => {}}
        />
      </div>
    );
  }

  // Show limit reached message if user has reached their limit
  if (usageLimitReached && usageData) {
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1, 1);
    const resetDate = nextMonth.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg border border-red-200 p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {t('consultation.limitReachedTitle', 'Limite mensuelle atteinte')}
            </h1>
            <p className="text-gray-600 mb-6">
              {t('consultation.limitReachedMessage', `Vous avez atteint votre limite mensuelle de ${usageData.limit} consultations. Vous ne pouvez plus effectuer de nouvelles consultations jusqu'au 1er du mois prochain.`)}
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="text-sm text-gray-600 mb-2">
                {t('consultation.usageDetails', 'Détails de votre utilisation')}
              </div>
              <div className="text-2xl font-bold text-red-600">
                {usageData.current} / {usageData.limit}
              </div>
              <div className="text-sm text-gray-500">
                {t('consultation.resetDate', 'Remise à zéro le')} {resetDate}
              </div>
            </div>
            <button
              onClick={() => navigate('/statistics')}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              {t('consultation.viewStatistics', 'Voir les statistiques')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {(isGenerating || isTranslating || isProcessingFinalTranscript || isFormattingTranscript) && (
        <LoadingOverlay 
          message={
            isTranslating 
              ? t('consultation.loading.translating')
              : isFormattingTranscript
              ? t('consultation.loading.formattingTranscript')
              : t('consultation.loading.generatingReport')
          } 
        />
      )}
      {showTranslation && translatedText && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowTranslation(false)} />
          <div className="fixed inset-0 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl p-6 m-4 max-w-2xl w-full relative">
              <div className="mb-4 flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('consultation.translation.title')}
                </h3>
                <button
                  onClick={() => setShowTranslation(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: translatedText }} />
            </div>
          </div>
        </div>
      )}
      <div className="h-full flex flex-col bg-white">
        <ErrorNotification
          show={!!error}
          message={error}
          onClose={() => setError('')}
        />
        
        {/* Header with steps */}
        <div className="px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-semibold text-gray-900">{t('consultation.title')}</h1>
            {subRoles.length > 1 && (
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">{t('consultation.role')}</span>
                <div className="relative w-48">
                  <Listbox value={selectedRole} onChange={setSelectedRole}>
                    {({ open }) => (
                      <>
                        <Listbox.Button className="relative w-full text-left bg-[#E0EAFE] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <span className="block truncate pr-8">{getRoleLabel(`role-${selectedRole.toLowerCase()}`)}</span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronDownIcon
                              className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                                open ? 'transform rotate-180' : ''
                              }`}
                              aria-hidden="true"
                            />
                          </span>
                        </Listbox.Button>

                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg max-h-60 py-1 text-sm ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
                            {subRoles.map((role) => (
                              <Listbox.Option
                                key={role}
                                value={role}
                                className={({ active, selected }) =>
                                  `relative cursor-pointer select-none py-2 px-3 ${
                                    active ? 'bg-[#E0EAFE] text-gray-900' : 'text-gray-900'
                                  } ${selected ? 'bg-[#E0EAFE]' : ''}`
                                }
                              >
                                {({ selected }) => (
                                  <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                    {getRoleLabel(`role-${role.toLowerCase()}`)}
                                  </span>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </>
                    )}
                  </Listbox>
                </div>
              </div>
            )}
          </div>

          {/* Steps */}
          <div className="bg-gray-100 rounded-3xl mt-12 shadow-sm">
            <div className="px-4 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-start space-x-12">
                  {steps.map((step) => (
                    <div 
                      key={step.id} 
                      className="flex flex-col items-start"
                      onClick={() => handleStepClick(step.id)}
                      role="button"
                      tabIndex={0}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className={`flex flex-col items-start rounded-2xl px-4 py-2 transition-colors duration-200 ${
                        step.id === currentStep 
                          ? 'bg-[#B4E4E1]' 
                          : 'hover:bg-[rgb(197,214,248)]'
                      }`}>
                        <div className="flex items-center text-gray-500">
                          <span className="text-[10px] uppercase mr-1">STEP</span>
                          <span className="text-[10px]">{step.id}</span>
                        </div>
                        <span className="text-sm text-gray-900 mt-1">{step.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center space-x-4">
                  <Tooltip content={t('consultation.resetTooltip')}>
                    <button
                      onClick={handleReset}
                      className="p-2 rounded-full hover:bg-[rgb(197,214,248)] transition-colors"
                    >
                      <ArrowPathIcon className="w-6 h-6 text-gray-600" />
                    </button>
                  </Tooltip>

                  <div className="relative">
                    <Tooltip content={t('consultation.steps.help')}>
                      <button
                        onClick={() => setIsHelpOpen(!isHelpOpen)}
                        className="p-2 rounded-full hover:bg-[rgb(197,214,248)] transition-colors"
                      >
                        <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </Tooltip>
                    
                    <HelpPopover
                      isOpen={isHelpOpen}
                      onClose={() => setIsHelpOpen(false)}
                      isWide={currentStep === 2 || currentStep === 3 || currentStep === 4 || currentStep === 5}
                      content={{
                        title: currentStep === 2 
                          ? t('consultation.help.dictaphone.title')
                          : currentStep === 3
                          ? ((selectedRole === 'osteo' || selectedRole === 'physio') ? t('consultation.help.fileReportPhysio.title') : t('consultation.help.fileReport.title'))
                          : currentStep === 4
                          ? ((selectedRole === 'osteo' || selectedRole === 'physio') ? t('consultation.help.patientReportPhysio.title') : t('consultation.help.patientReport.title'))
                          : currentStep === 5
                          ? ((selectedRole === 'osteo' || selectedRole === 'physio') ? t('consultation.help.quotePhysio.title') : t('consultation.help.quote.title'))
                          : t('consultation.help.title'),
                        step1: currentStep === 1 
                          ? t('consultation.help.step1')
                          : currentStep === 2
                          ? t('consultation.help.dictaphone.description')
                          : currentStep === 3
                          ? ((selectedRole === 'osteo' || selectedRole === 'physio') ? t('consultation.help.fileReportPhysio.description') : t('consultation.help.fileReport.description'))
                          : currentStep === 4
                          ? ((selectedRole === 'osteo' || selectedRole === 'physio') ? t('consultation.help.patientReportPhysio.description') : t('consultation.help.patientReport.description'))
                          : currentStep === 5
                          ? ((selectedRole === 'osteo' || selectedRole === 'physio') ? t('consultation.help.quotePhysio.description') : t('consultation.help.quote.description'))
                          : t('consultation.help.step1'),
                        step2: currentStep === 1
                          ? t('consultation.help.step2')
                          : currentStep === 2
                          ? t('consultation.help.dictaphone.verify')
                          : currentStep === 3
                          ? ((selectedRole === 'osteo' || selectedRole === 'physio') ? t('consultation.help.fileReportPhysio.verify') : t('consultation.help.fileReport.verify'))
                          : currentStep === 4
                          ? ((selectedRole === 'osteo' || selectedRole === 'physio') ? t('consultation.help.patientReportPhysio.verify') : t('consultation.help.patientReport.verify'))
                          : currentStep === 5
                          ? ((selectedRole === 'osteo' || selectedRole === 'physio') ? t('consultation.help.quotePhysio.verify') : t('consultation.help.quote.verify'))
                          : t('consultation.help.step2')
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1">
          <div className="flex h-full overflow-hidden">
            {/* Main Panel */}
            <div className={`${currentStep === 1 ? 'w-2/3' : 'w-full'} p-6`}>
              <div className="bg-gray-100 rounded-3xl shadow-sm h-full flex flex-col">
                {/* Content Header */}
                <div className="pl-6 pt-6 pr-6">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {currentStep === 1 && t('consultation.transcription.title')}
                      {currentStep === 2 && t('consultation.dictaphone.title')}
                      {currentStep === 3 && ((selectedRole === 'osteo' || selectedRole === 'physio') ? t('consultation.fileReportPhysio') : t('consultation.fileReport'))}
                      {currentStep === 4 && ((selectedRole === 'osteo' || selectedRole === 'physio') ? t('consultation.patientReportPhysio') : t('consultation.patientReport'))}
                      {currentStep === 5 && ((selectedRole === 'osteo' || selectedRole === 'physio') ? t('consultation.quotePhysio') : t('consultation.quote'))}
                    </h2>
                    {(currentStep === 2 || currentStep === 3 || currentStep === 4 || currentStep === 5) && (
                      <div className="flex items-center space-x-2">
                        {currentStep === 5 && (
                          <button
                            onClick={handleGenerateQuotePDF}
                            disabled={isGenerating || !quoteText || quoteText.trim() === ''}
                            className={`inline-flex items-center justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 
                              ${!isGenerating && quoteText && quoteText.trim() !== '' ? 'hover:bg-[rgb(121,192,189)]' : 'opacity-50 cursor-not-allowed'}
                              focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2`}
                          >
                            <ArrowDownTrayIcon className="w-5 h-5 mr-1.5" />
                            {t('consultation.quoteDownloadPdf')}
                          </button>
                        )}
                        <button
                          onClick={translatedText ? handleCopyTranslation : handleCopy}
                          className={`inline-flex items-center justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 
                            ${!copied ? 'hover:bg-[rgb(121,192,189)]' : 'opacity-75'}
                            focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2`}
                        >
                          {copied || showCopySuccess ? (
                            <>
                              <CheckIcon className="w-5 h-5 mr-1.5" />
                              {t('common.success')}
                            </>
                          ) : (
                            <>
                              <DocumentDuplicateIcon className="w-5 h-5 mr-1.5" />
                              {t('common.copy')}
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Content based on current step */}
                {renderEditor()}
              </div>
            </div>

            {/* Right Panel - Keywords (1/3) - Only visible in step 1 */}
            {currentStep === 1 && (
              <div className="w-1/3 p-6">
                <div className="bg-gray-100 rounded-3xl shadow-sm h-full flex flex-col">
                  <div className="p-6">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {t('consultation.memo')}
                    </h2>
                    <p className="text-sm text-gray-600 mt-2">
                      {t('consultation.memoDescription')}
                    </p>
                  </div>
                  
                  <div className="flex-1 p-6 overflow-y-auto">
                    {loading ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                      </div>
                    ) : keywords.length === 0 ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center text-gray-500">
                          <div className="w-12 h-12 mx-auto mb-3">
                            <svg className="w-full h-full text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                            </svg>
                          </div>
                          <p className="text-sm">{t('settings.keywords.noKeywords')}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="flex flex-wrap gap-2">
                          {keywords.map((item, index) => {
                            // Vérifie si au moins un mot-clé est trouvé dans la transcription
                            const isAnyKeywordFound = item.keywords
                              .split(',')
                              .some(keyword => 
                                transcriptSegments.join(' ').toLowerCase().includes(keyword.trim().toLowerCase())
                              );
                            
                            return (
                              <span
                                key={index}
                                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                  isAnyKeywordFound 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-300 text-gray-700'
                                }`}
                              >
                                {item.description}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Edit Button */}
                  <div className="p-6">
                    <div className="flex justify-end">
                      <Tooltip content={t('settings.keywords.edit')}>
                        <button
                          onClick={handleEditKeywords}
                          className="p-3 rounded-full hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(141,212,209)]"
                        >
                          <PencilSquareIcon className="h-6 w-6 text-gray-600" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Consultation; 